import MenuIcon from '@mui/icons-material/Menu'
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  alpha,
  Divider,
} from '@mui/material'
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar'
import IconButton from '@mui/material/IconButton'
import { styled, useTheme } from '@mui/material/styles'
import Toolbar from '@mui/material/Toolbar'
import React, { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ITables } from '../all-types'
import { CommonBtn, PrinterStatusHeader } from '../components'
import SearchInput from '../components/Input/SearchInput/SearchInput'
import { deliveryOptions, STYLE_CONSTANTS } from '../constants'
import { usePrinterSettings, useProductSearch } from '../hooks'
import { SocketConnectionBadge } from '../realtime'
import { setSelectedFloor, setSelectedTable } from '../redux-store'
import { useAppDispatch, useAppSelector } from '../redux-store/hooks'
import { ConnectionStatus } from '../screens'
import { updateCartItemOfTheTable } from '../services'

interface AppBarProps extends MuiAppBarProps {
  open?: boolean
}

interface HeaderProps {
  open: boolean
  handleDrawerOpen: () => void
  isLoaderActive: boolean
}

// ---- Styled Components for Premium Look ----

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: alpha(theme.palette.background.paper, 0.85),
  backdropFilter: 'blur(12px) saturate(160%)',
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  boxShadow: 'none',
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: STYLE_CONSTANTS.DRAWER_WIDTH,
    width: `calc(100% - ${STYLE_CONSTANTS.DRAWER_WIDTH}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}))

const SearchContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  maxWidth: '500px',
  marginLeft: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    backgroundColor: alpha(theme.palette.action.hover, 0.05),
    borderRadius: '12px',
    '&:hover': {
      backgroundColor: alpha(theme.palette.action.hover, 0.1),
    },
  }
}))

// Helper Sub-component for Toggle-style buttons
const TabButton = ({ active, children, onClick }: { active: boolean, children: React.ReactNode, onClick: () => void }) => {
  const theme = useTheme();
  return (
    <CommonBtn
      size="small"
      onClick={onClick}
      sx={{
        px: 2,
        py: 0.7,
        borderRadius: '10px',
        textTransform: 'uppercase',
        fontSize: '0.75rem',
        letterSpacing: '0.5px',
        fontWeight: 800,
        boxShadow: 'none',
        bgcolor: active ? theme.palette.primary.main : 'transparent',
        color: active ? theme.palette.primary.contrastText : theme.palette.text.secondary,
        '&:hover': {
          bgcolor: active ? theme.palette.primary.dark : alpha(theme.palette.primary.main, 0.08),
          boxShadow: active ? theme.shadows[2] : 'none',
        }
      }}
    >
      {children}
    </CommonBtn>
  )
}

const Header: React.FC<HeaderProps> = ({
  open,
  handleDrawerOpen,
  isLoaderActive,
}) => {
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const queryParams = new URLSearchParams(location.search)
  
  const tab = queryParams.get('tab') || 'ongoing'
  const isTableView = tab === 'table'
  const isOngoingOrders = tab === 'ongoing'
  const isBillingHistory = tab === 'billing-history'
  const isNewTakeAway = tab === 'new-take-away'
  
  const allFloors = useAppSelector((state) => state.preloadData.floors)
  const selectedFloor = useAppSelector((state) => state.preloadData.selectedFloor)
  const selectedTable = useAppSelector((state) => state.table.selectedTable)
  
  const isDashboard = location.pathname === '/app'
  const isCounterRoute = location.pathname === '/app/counter'
  const isSettingTab = location.pathname === '/app/settings'
  const isKitchenRoute = location.pathname === '/app/kitchen'
  
  const isTakeAwayTable = selectedTable?._id === '999'
  const showFloorSelector = isCounterRoute && !selectedTable
  const showtableInfo = isCounterRoute && selectedFloor && selectedTable
  const showTakeAway = isCounterRoute && isNewTakeAway && selectedTable
  
  const { printerStatus } = usePrinterSettings()
  const { searchTxt, searchTheProduct } = useProductSearch()

  const handleFloorChange = (event: SelectChangeEvent) => {
    const floorId = event.target.value
    const floor = allFloors.find((f) => f._id === floorId)
    if (floor) dispatch(setSelectedFloor(floor))
  }

  useEffect(() => {
    if (allFloors && allFloors.length > 0 && isTableView) {
      dispatch(setSelectedFloor(allFloors[0]))
    }
  }, [tab])

  const closeTable = async () => {
    try {
      if (selectedTable && selectedTable?.billSession) {
        if (selectedTable?.billSession?.items?.length === 0) {
          const clonedTable: ITables = JSON.parse(JSON.stringify(selectedTable))
          clonedTable.billSession = null
          await dispatch(updateCartItemOfTheTable({ table: clonedTable }))
        }
      }
    } catch (e) {
      console.error(e)
    } finally {
      await dispatch(setSelectedTable(null))
    }
  }

  return (
    <AppBar position="fixed" open={open}>
      {isLoaderActive && (
        <LinearProgress
          color="primary"
          sx={{ position: 'absolute', top: 0, width: '100%', height: 2, zIndex: 2 }}
        />
      )}
      
      <Toolbar sx={{ height: 70, px: 2 }}>
        <IconButton
          color="inherit"
          onClick={handleDrawerOpen}
          edge="start"
          sx={{
            marginRight: 2,
            color: theme.palette.text.primary,
            ...(open && { display: 'none' }),
          }}
        >
          <MenuIcon />
        </IconButton>

        <Grid container alignItems="center" wrap="nowrap">
          {/* --- Left Side: Titles --- */}
          {(isSettingTab || isKitchenRoute) && (
             <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 900, color: 'text.primary', letterSpacing: -0.5 }}>
                  {isSettingTab ? 'SETTINGS' : 'KITCHEN'}
                </Typography>
                {isKitchenRoute && (
                   <Typography variant="caption" sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.2), color: 'secondary.main', px: 1, py: 0.3, borderRadius: 1, fontWeight: 900 }}>
                    EXPRESS (FAST)
                  </Typography>
                )}
             </Stack>
          )}

          {/* --- Center Side: Navigation or Search --- */}
          {isCounterRoute && (
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              {(!showtableInfo || isOngoingOrders) && !isTakeAwayTable ? (
                <Stack 
                  direction="row" 
                  spacing={0.5} 
                  sx={{ 
                    bgcolor: alpha(theme.palette.text.primary, 0.04), 
                    p: 0.6, 
                    borderRadius: '14px',
                    border: `1px solid ${alpha(theme.palette.divider, 0.05)}` 
                  }}
                >
                  <TabButton active={isOngoingOrders} onClick={() => navigate('/app/counter?tab=ongoing')}>Ongoing</TabButton>
                  <TabButton active={isTableView} onClick={() => navigate('/app/counter?tab=table')}>Dine-In</TabButton>
                  <TabButton active={isNewTakeAway} onClick={() => navigate('/app/counter?tab=new-take-away')}>Take-Away</TabButton>
                  <TabButton active={isBillingHistory} onClick={() => navigate('/app/counter?tab=billing-history')}>History</TabButton>
                </Stack>
              ) : (
                <SearchContainer>
                  <SearchInput
                    value={searchTxt}
                    onChange={(txt) => searchTheProduct(txt)}
                    placeholder="Search Name, ID, or Category..."
                  />
                </SearchContainer>
              )}
            </Box>
          )}

          {/* --- Right Side: Floor Selector / Table Info / Health Status --- */}
          <Stack direction="row" alignItems="center" spacing={2} sx={{ ml: 'auto' }}>
            
            {isTableView &&  showFloorSelector && (
              <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="floor-select-label">Floor</InputLabel>
                <Select
                  labelId="floor-select-label"
                  value={selectedFloor?._id ?? ''}
                  onChange={handleFloorChange}
                  label="Floor"
                  sx={{ borderRadius: '10px' }}
                >
                  {allFloors.map((floor) => (
                    <MenuItem key={floor._id} value={floor._id}>
                      Floor {floor.floorNo}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {(showtableInfo || (showTakeAway && isTakeAwayTable)) && (
              <Stack 
                direction="row" 
                alignItems="center" 
                spacing={2} 
                sx={{ 
                  bgcolor: 'primary.main', 
                  color: 'primary.contrastText', 
                  px: 2, 
                  py: 1, 
                  borderRadius: '12px',
                  boxShadow: `0 4px 14px 0 ${alpha(theme.palette.primary.main, 0.35)}`
                }}
              >
                 <Stack direction="row" spacing={1} alignItems="center">
                    {isTakeAwayTable && selectedTable.billSession?.takeAwayType && (
                      <Box 
                        component="img" 
                        src={deliveryOptions[selectedTable.billSession?.takeAwayType]?.logoUrl} 
                        sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'white' }} 
                      />
                    )}
                    <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                      {isTakeAwayTable ? selectedTable.billSession?.takeAwayType : `TABLE ${selectedTable.tableNo}`}
                    </Typography>
                 </Stack>
                 <Divider orientation="vertical" flexItem sx={{ borderColor: alpha('#fff', 0.2) }} />
                 <CommonBtn
                    variant="contained"
                    color='error'
                    size="small"
                    onClick={isTakeAwayTable ? () => dispatch(setSelectedTable(null)) : closeTable}
                    sx={{ color: '#fff', fontWeight: 800, minWidth: 'auto', p: 0 }}
                  >
                    CLOSE
                  </CommonBtn>
              </Stack>
            )}

            {/* --- Health & Connectivity Cluster --- */}
            <Stack 
              direction="row" 
              alignItems="center" 
              spacing={1.5} 
              sx={{ 
                ml: 1, 
                pl: 2, 
                borderLeft: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                display: { xs: 'none', md: 'flex' } 
              }}
            >
              <SocketConnectionBadge />
              <ConnectionStatus compact />
              <PrinterStatusHeader
                printerStatus={printerStatus}
                compact={isCounterRoute || isKitchenRoute}
              />
            </Stack>
          </Stack>
        </Grid>
      </Toolbar>
    </AppBar>
  )
}

export default Header